import { getActiveModule, getModuleConfig } from "../moduleConfig";

export const THEME_PRESETS = [
  { id: "ocean", label: "Océan", color: "#2563eb" },
  { id: "emerald", label: "Émeraude", color: "#059669" },
  { id: "violet", label: "Violet", color: "#7c3aed" },
  { id: "sunset", label: "Corail", color: "#e85d3f" },
  { id: "terracotta", label: "Terre cuite", color: "#98503f" },
  { id: "midnight", label: "Minuit", color: "#0f3d5e" }
];

const DEFAULT_LOGO = `${process.env.REACT_APP_APIEVIVESOMA || ""}/files/evivesoma.png`;
const GLOBAL_THEME_KEY = "evivesoma-theme";
const THEME_KEY_PREFIX = `${GLOBAL_THEME_KEY}-`;

const readJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key));
  } catch {
    return null;
  }
};

const normalizeHex = (value) => {
  if (!value) return null;
  const color = String(value).trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color : null;
};

const mix = (hex, target, amount) => {
  const source = hex.replace("#", "");
  const destination = target.replace("#", "");
  const channel = (index) => Math.round(
    parseInt(source.slice(index, index + 2), 16) * (1 - amount)
    + parseInt(destination.slice(index, index + 2), 16) * amount
  ).toString(16).padStart(2, "0");
  return `#${channel(0)}${channel(2)}${channel(4)}`;
};

export const getEstablishment = () => {
  const establishment = readJson("establishment");
  return establishment || {};
};

export const getBranding = () => {
  const establishment = getEstablishment();
  const moduleConfig = getModuleConfig();
  const appName = process.env.REACT_APP_NAME || "EviveSoma";
  const establishmentName = establishment.name
    || process.env.REACT_APP_ESTABLISHMENT_NAME
    || "";

  return {
    appName,
    establishmentName,
    displayName: establishmentName || appName,
    shortName: establishment.short_name || establishmentName || appName,
    logo: establishment.logo || process.env.REACT_APP_ESTABLISHMENT_LOGO || DEFAULT_LOGO || "/logo192.png",
    slogan: establishment.slogan || "Gérez votre établissement avec sérénité",
    moduleLabel: moduleConfig.label,
    countryCode: establishment.countryCode,
    type : establishment.type || process.env.REACT_APP_ESTABLISHMENT_TYPE || "2",
    moduleKey: getActiveModule(),
    establishmentId: establishment.id || "default"
  };
};

const preferenceKey = () => `evivesoma-theme-${getBranding().establishmentId}`;

export const getThemeColor = () => {
  const establishment = getEstablishment();
  const stored = normalizeHex(window.localStorage.getItem(preferenceKey()));
  const lastUsed = normalizeHex(window.localStorage.getItem(GLOBAL_THEME_KEY));
  const configured = normalizeHex(
    establishment.primary_color
    || establishment.color
    || establishment.theme_color
    || process.env.REACT_APP_PRIMARY_COLOR
  );
  const fallback = establishment.id
    ? configured || lastUsed
    : lastUsed || configured;
  return stored || fallback || THEME_PRESETS[0].color;
};

export const applyBrandingTheme = (color = getThemeColor()) => {
  const primary = normalizeHex(color) || THEME_PRESETS[0].color;
  const root = document.documentElement;
  root.style.setProperty("--ev-primary", primary);
  root.style.setProperty("--ev-primary-dark", mix(primary, "#071426", 0.38));
  root.style.setProperty("--ev-primary-soft", mix(primary, "#ffffff", 0.9));
  root.style.setProperty("--ev-primary-glow", `${primary}2e`);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", primary);
  return primary;
};

export const saveThemeColor = (color) => {
  const primary = normalizeHex(color);
  if (!primary) return;
  window.localStorage.setItem(preferenceKey(), primary);
  window.localStorage.setItem(GLOBAL_THEME_KEY, primary);
  applyBrandingTheme(primary);
  window.dispatchEvent(new CustomEvent("evivesoma:theme", { detail: primary }));
};

export const clearAppStoragePreservingTheme = () => {
  const themePreferences = {};

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    const value = key && normalizeHex(window.localStorage.getItem(key));
    if (key && (key === GLOBAL_THEME_KEY || key.startsWith(THEME_KEY_PREFIX)) && value) {
      themePreferences[key] = value;
    }
  }

  const activeColor = getThemeColor();
  window.localStorage.clear();

  Object.entries(themePreferences).forEach(([key, value]) => {
    window.localStorage.setItem(key, value);
  });
  window.localStorage.setItem(GLOBAL_THEME_KEY, activeColor);
  applyBrandingTheme(activeColor);
};
